import { useQueries } from '@tanstack/react-query';
import { BarChart3, LineChart, Printer } from 'lucide-react';
import { useMemo, useState } from 'react';

import { getHistoricoControllerSerieQueryOptions } from '@/api/generated/historico/historico';
import type { AnalitoDoHistoricoResponse, SerieDoAnalitoResponse } from '@/api/generated/model';
import { GraficoDeBarrasDoExame } from '@/features/historico/components/GraficoDeBarrasDoExame';
import { GraficoDeEvolucao } from '@/features/historico/components/GraficoDeEvolucao';
import { SeletorDeAnalito } from '@/features/historico/components/SeletorDeAnalito';
import { TabelaComparativa } from '@/features/historico/components/TabelaComparativa';
import { EstadoErro } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * `exame` = barras agrupadas com todos os analitos; `analito` = a linha de um só.
 */
type Modo = 'exame' | 'analito';

interface Props {
  pacienteId: string;
  /** Analitos com ao menos uma medição, do resumo do histórico. */
  analitos: AnalitoDoHistoricoResponse[];
  /** Só para o cabeçalho impresso — ver o botão Imprimir mais abaixo. */
  paciente: { nome: string; especie: string; idade: string; tutorNome: string | null };
}

/**
 * Análise dos resultados ao longo do tempo (F6-1).
 *
 * ⚠️ **Exame com mais de um resultado abre em BARRAS AGRUPADAS.** Uma barra por
 * analito em cada data, todas no mesmo eixo — é assim que se lê "Cortisol Basal
 * e Pós-ACTH", "T4 total e T4 livre", perfil renal: os valores vêm do mesmo tubo
 * e o que importa é a relação entre eles naquele dia. Antes só existia a linha de
 * UM analito, e comparar dois exigia trocar a seleção de ida e volta guardando o
 * número de cabeça. A linha continua a um clique, e é o padrão de quem tem
 * resultado único — evolução de um valor é linha, não barra.
 *
 * ⚠️ O agrupamento é por `catalogoExameId`, nunca por `exameNome`: o nome do
 * exame do catálogo não é único, e dois homônimos cairiam no mesmo gráfico.
 *
 * Fica em componente próprio (e não dentro do `HistoricoPage`) porque a
 * `TabsContent` do Radix desmonta a aba inativa: assim as séries só são buscadas
 * de quem abriu esta aba.
 */
export function AbaEvolucao({ pacienteId, analitos, paciente }: Props) {
  const [analitoId, setAnalitoId] = useState('');
  const [modo, setModo] = useState<Modo>('exame');

  // O primeiro da lista é o de medição mais recente — é o que o médico quer ver
  // ao abrir. Só depois de escolher é que `analitoId` manda.
  const selecionadoId = analitoId || analitos[0]?.analitoId || '';
  const selecionado = analitos.find((a) => a.analitoId === selecionadoId);

  // Os analitos do MESMO exame do catálogo, na ordem em que o laudo os imprime.
  const doExame = useMemo(() => {
    if (!selecionado) return [];

    return analitos
      .filter((a) => a.catalogoExameId === selecionado.catalogoExameId)
      .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [analitos, selecionado]);

  // Sem um segundo analito não existe barra agrupada para desenhar: o modo cai
  // para a linha, sem depender do que o usuário escolheu antes.
  const modoEfetivo: Modo = doExame.length > 1 ? modo : 'analito';

  const alvos = modoEfetivo === 'exame' ? doExame : selecionado ? [selecionado] : [];

  // `useQueries` porque o número de séries é o número de analitos do exame. A
  // chave é a mesma da consulta de um analito só, então alternar entre os dois
  // modos reaproveita o que já está em cache em vez de ir ao servidor de novo.
  const consultas = useQueries({
    queries: alvos.map((analito) =>
      getHistoricoControllerSerieQueryOptions(pacienteId, analito.analitoId),
    ),
  });

  const carregando = consultas.some((consulta) => consulta.isPending);
  const comErro = consultas.some((consulta) => consulta.isError);

  const series = consultas
    .map((consulta) => consulta.data)
    .filter((serie): serie is SerieDoAnalitoResponse => serie !== undefined);

  const serieSelecionada = series.find((serie) => serie.analitoId === selecionadoId);

  const titulo =
    modoEfetivo === 'exame'
      ? (selecionado?.exameNome ?? 'Evolução')
      : (serieSelecionada?.nome ?? 'Evolução');

  return (
    <div className="space-y-4">
      {/* Só existe no papel — a tela nunca mostra isto. Substitui o
          breadcrumb/badges da `HistoricoPage` (marcados `print:hidden`), que
          falam da navegação da tela, não do paciente do relatório. Logo fixa
          na variante clara: página impressa é sempre fundo branco, então a
          marca pensada para tema escuro ficaria sem contraste. */}
      <div className="hidden print:flex items-center justify-between gap-4 border-b-2 border-brand pb-3 mb-2">
        <img src="/logo-nohra-light.png" alt="NOHRA Clinic" className="h-12 w-auto object-contain" />
        <div className="text-right text-sm text-black">
          <p className="font-semibold">{paciente.nome}</p>
          <p className="text-xs">
            {[paciente.especie, paciente.idade, paciente.tutorNome && `Tutor: ${paciente.tutorNome}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <p className="text-xs text-neutral-500">
            Emitido em{' '}
            {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 print:hidden">
        <SeletorDeAnalito analitos={analitos} valor={selecionadoId} aoEscolher={setAnalitoId} />

        {doExame.length > 1 && (
          <div
            role="group"
            aria-label="Como desenhar os resultados"
            className="flex rounded-md border border-input overflow-hidden self-start"
          >
            <BotaoDeModo
              ativo={modo === 'exame'}
              icone={BarChart3}
              rotulo={`Exame (${doExame.length} resultados)`}
              aoClicar={() => setModo('exame')}
            />
            <BotaoDeModo
              ativo={modo === 'analito'}
              icone={LineChart}
              rotulo="Só este exame"
              aoClicar={() => setModo('analito')}
            />
          </div>
        )}

        <Button
          variant="outline"
          className="sm:ml-auto gap-2"
          onClick={() => window.print()}
          disabled={carregando || comErro || series.length === 0}
        >
          <Printer className="w-4 h-4" />
          Imprimir
        </Button>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">{titulo}</h3>

          {/* A tendência é de UM analito, então só aparece no modo do analito:
              anunciá-la ao lado do exame inteiro faria parecer que ela vale para
              todas as barras. */}
          {modoEfetivo === 'analito' && serieSelecionada?.tendencia && (
            <span className="text-xs text-muted-foreground">
              Da primeira à última medição:{' '}
              {serieSelecionada.tendencia.variacaoPercentual === null
                ? serieSelecionada.tendencia.direcao.toLowerCase()
                : `${
                    serieSelecionada.tendencia.variacaoPercentual > 0 ? '+' : ''
                  }${serieSelecionada.tendencia.variacaoPercentual.toLocaleString('pt-BR', {
                    maximumFractionDigits: 1,
                  })}%`}
            </span>
          )}
        </div>

        {carregando ? (
          <div className="p-6">
            <Skeleton className="h-56 rounded-md" />
          </div>
        ) : comErro || series.length === 0 ? (
          <EstadoErro
            aoTentarDeNovo={() => consultas.forEach((consulta) => void consulta.refetch())}
          />
        ) : (
          <div className="p-6 space-y-6">
            {modoEfetivo === 'exame' ? (
              <GraficoDeBarrasDoExame series={series} />
            ) : (
              serieSelecionada && <GraficoDeEvolucao serie={serieSelecionada} />
            )}

            {/* No modo exame, a tabela acompanha o gráfico: um bloco por
                analito das barras, não só o escolhido no seletor — senão a
                pessoa vê duas barras comparadas e só consegue conferir os
                números de uma.

                Mesma solução do `EvolucaoDoPacienteDialogo`, que mostra este
                mesmo par gráfico+tabela dentro da discussão. */}
            {modoEfetivo === 'exame'
              ? series.map((serie) => (
                  <div key={serie.analitoId}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {serie.nome}
                    </p>
                    <TabelaComparativa serie={serie} />
                  </div>
                ))
              : serieSelecionada && <TabelaComparativa serie={serieSelecionada} />}
          </div>
        )}
      </div>

      {/* Rodapé só do papel, mesma lógica da logo do cabeçalho acima. */}
      <div className="hidden print:flex items-center justify-center gap-2 border-t border-neutral-300 pt-3 mt-4 text-xs text-neutral-500">
        <span>Feito com Nohra Clinic</span>
        <img src="/logo-nohra-light.png" alt="NOHRA Clinic" className="h-5 w-auto object-contain" />
      </div>
    </div>
  );
}

function BotaoDeModo({
  ativo,
  icone: Icone,
  rotulo,
  aoClicar,
}: {
  ativo: boolean;
  icone: typeof BarChart3;
  rotulo: string;
  aoClicar: () => void;
}) {
  return (
    <button
      type="button"
      // `aria-pressed`, como os botões de visualização da lista de pacientes:
      // leitor de tela precisa saber QUAL desenho está ativo, não só que há dois.
      aria-pressed={ativo}
      onClick={aoClicar}
      className={cn(
        'flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors',
        ativo ? 'bg-brand text-brand-foreground' : 'text-foreground hover:bg-muted',
      )}
    >
      <Icone className="w-4 h-4" aria-hidden="true" />
      {rotulo}
    </button>
  );
}
